import React from "react";
import { useNavigate } from "react-router-dom";
import useCurrentEmployee from "../../hooks/useCurrentEmployee";
import PageIntroNav from "../../components/navigation/PageIntroNav";
import FetchLoadingState from "../../components/feedback/FetchLoadingState";
import { FaClipboard } from "react-icons/fa";

const QueryManagement = () => {
  const navigate = useNavigate();
  const { companyAccount, currentEmployee, loading } = useCurrentEmployee();
  const isAdmin = companyAccount || currentEmployee?.modulePermissions?.includes("query-management");
  const isLoading = !companyAccount && loading;

  if (isLoading) {
    return <FetchLoadingState message="Loading..." />;
  }

  return (
    <div className="dashboard-enter p-6">
      <PageIntroNav
        breadcrumbs={[{ label: "Query Management" }]}
        title="Query Management & Disciplinary Status"
        subtitle="Manage queries, formal warnings, suspensions, acknowledgements, and employee responses."
        fallbackPath="/"
        backLabel="Back to Dashboard"
      />

      <div className="">
        <div className="grid gap-6 md:grid-cols-2 dashboard-enter dashboard-enter-delay-1">
          {/* Admin View Card */}
          {isAdmin && (
            <div
              onClick={() => navigate("/query-management/admin")}
              className="group cursor-pointer rounded-2xl border border-gray-200 bg-gradient-to-br from-blue-50 to-blue-100 p-8 shadow-sm transition dashboard-card-hover stagger-item"
            >
              <div className="mb-4 inline-block rounded-lg bg-blue-200 p-4">
                <FaClipboard className="text-2xl text-blue-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900">
                Manage Disciplinary Status
              </h3>
              <p className="mt-2 text-sm text-gray-600">
                Create queries, warnings, and suspension notices. Track delivery,
                responses, status, and resolution history.
              </p>
              <div className="mt-4 inline-flex items-center text-sm font-medium text-blue-600 group-hover:translate-x-1 transition">
                Go to Admin Panel →
              </div>
            </div>
          )}

          {/* Employee View Card */}
          <div
            onClick={() => navigate("/query-management/my-queries")}
            className="group cursor-pointer rounded-2xl border border-gray-200 bg-gradient-to-br from-green-50 to-green-100 p-8 shadow-sm transition dashboard-card-hover stagger-item"
          >
            <div className="mb-4 inline-block rounded-lg bg-green-200 p-4">
              <FaClipboard className="text-2xl text-green-600" />
            </div>
            <h3 className="text-xl font-semibold text-gray-900">My Disciplinary Status</h3>
            <p className="mt-2 text-sm text-gray-600">
              View queries, warnings, and suspension notices sent to you, then
              acknowledge or submit a response.
            </p>
            <div className="mt-4 inline-flex items-center text-sm font-medium text-green-600 group-hover:translate-x-1 transition">
              View My Queries →
            </div>
          </div>
        </div>

        {/* Info Section */}
        <div className="mt-12 rounded-2xl bg-gradient-to-r from-purple-50 to-pink-50 p-8 dashboard-enter dashboard-enter-delay-2">
          <h3 className="text-lg font-semibold text-gray-900">
            How Disciplinary Status Works
          </h3>
          <div className="mt-4 grid gap-4 md:grid-cols-2">
            <div className="smooth-transition hover:translate-y-1">
              <h4 className="font-medium text-gray-900">For Administrators</h4>
              <p className="mt-2 text-sm text-gray-600">
                • Create organizational queries
              </p>
              <p className="text-sm text-gray-600">
                • Serve to specific employees
              </p>
              <p className="text-sm text-gray-600">
                • Track responses and status
              </p>
            </div>
            <div className="smooth-transition hover:translate-y-1">
              <h4 className="font-medium text-gray-900">For Employees</h4>
              <p className="mt-2 text-sm text-gray-600">
                • Receive queries via notification and email
              </p>
              <p className="text-sm text-gray-600">• View query details</p>
              <p className="text-sm text-gray-600">
                • Submit responses with attachments
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QueryManagement;
